// The desktop pane's dock (spec "Desktop navigation"): a strip under the stream, never over
// it, with Apps (open one of the machine's apps) and a chip per open window (click: to the
// front; ×, or a middle click: close it, as its own close button does). It looks again every
// 2 s while the pane shows, and right after a focus, a close or a launch. The same bar the iOS
// and Mac apps draw (DesktopWindows.swift).

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { closeWindow, desktopApps, desktopWindows, focusWindow, launchApp, type DesktopApp, type DesktopWindow } from '../net/desktop';
import { machineName, toast } from '../store';
import { IconLayoutGrid, IconX } from '@tabler/icons-react';
import { Ic, Mark, presence, usePresence } from './ds';
import { onFramePress } from './desktopLink';
import { COARSE_MQ, matches } from './mobile';

const POLL_MS = 2000;
/** While an app opens: look this often, and give up on seeing its window after this long. */
const OPENING_POLL_MS = 500;
const OPENING_MAX_MS = 15_000;
/** How long a closed window's chip takes to go. */
const LEAVE_MS = 180;
/** After a close: look again after these waits (it goes at once, or its app asks first). */
const CLOSE_LOOKS_MS = [120, 400, 900];
/** Above this many apps the launcher has a search field. */
const SEARCH_MIN = 12;

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const reducedMotion = () => matches('(prefers-reduced-motion: reduce)');

/** Same windows, same order, same state: nothing to redraw. */
const sameWindows = (a: DesktopWindow[], b: DesktopWindow[]) =>
  a.length === b.length && a.every((w, i) => w.id === b[i].id && w.active === b[i].active && w.title === b[i].title && w.app === b[i].app && w.icon === b[i].icon);

interface Opening {
  app: DesktopApp;
  /** The windows and the one in front when it was asked for: a new one (or a new front) means it opened. */
  before: Set<string>;
  active: string | undefined;
}

/** The desktop's windows, looked at every 2 s while `live`; focus, close and launch look again at once. */
export function useDesktopWindows(live: boolean) {
  const [windows, setWindows] = useState<DesktopWindow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [opening, setOpening] = useState<Opening | null>(null);
  // Windows asked to close: dimmed until they go, or until their app asks first.
  const [closing, setClosing] = useState<ReadonlySet<string>>(new Set());
  const windowsRef = useRef(windows);
  windowsRef.current = windows;
  // A reply to a look that started before a focus is stale: only the newest look lands.
  const seq = useRef(0);
  const inflight = useRef(0);

  const refresh = useCallback(async () => {
    const my = ++seq.current;
    inflight.current++;
    try {
      const w = await desktopWindows();
      if (my !== seq.current) return;
      setWindows((prev) => (sameWindows(prev, w) ? prev : w));
      setLoaded(true);
    } catch {
      /* disconnected: the next look tries again */
    } finally {
      inflight.current--;
    }
  }, []);

  useEffect(() => {
    if (!live) return;
    const look = () => document.visibilityState === 'visible' && inflight.current === 0 && void refresh();
    void refresh();
    const t = setInterval(look, POLL_MS);
    document.addEventListener('visibilitychange', look);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', look);
    };
  }, [live, refresh]);

  // Opening an app: look closely until its window shows, then the chip gives way to it.
  useEffect(() => {
    if (!opening) return;
    const active = windows.find((w) => w.active)?.id;
    if (windows.some((w) => !opening.before.has(w.id)) || active !== opening.active) {
      setOpening(null);
      return;
    }
    const t = setInterval(() => void refresh(), OPENING_POLL_MS);
    const give = setTimeout(() => setOpening(null), OPENING_MAX_MS);
    return () => {
      clearInterval(t);
      clearTimeout(give);
    };
  }, [opening, windows, refresh]);

  const focus = useCallback(
    async (w: DesktopWindow) => {
      seq.current++; // drop any look already on its way: it predates this
      setWindows((ws) => ws.map((x) => (x.active === (x.id === w.id) ? x : { ...x, active: x.id === w.id })));
      try {
        await focusWindow(w.id);
      } catch (e) {
        toast({ title: `Couldn’t switch to ${w.app}`, body: errMsg(e), kind: 'error' });
      }
      await refresh();
    },
    [refresh],
  );

  const close = useCallback(
    async (w: DesktopWindow) => {
      setClosing((c) => new Set(c).add(w.id));
      try {
        await closeWindow(w.id);
        // It goes at once, or its app asks first (unsaved work): the question shows up as a
        // window of its own, and this one stays.
        for (const ms of CLOSE_LOOKS_MS) {
          await new Promise((r) => setTimeout(r, ms));
          seq.current++; // a look already on its way predates the close
          await refresh();
          if (!windowsRef.current.some((x) => x.id === w.id)) break;
        }
      } catch (e) {
        toast({ title: `Couldn’t close ${w.app}`, body: errMsg(e), kind: 'error' });
      }
      setClosing((c) => {
        const next = new Set(c);
        next.delete(w.id);
        return next;
      });
    },
    [refresh],
  );

  const launch = useCallback(
    async (app: DesktopApp) => {
      const ws = windowsRef.current;
      setOpening({ app, before: new Set(ws.map((w) => w.id)), active: ws.find((w) => w.active)?.id });
      try {
        await launchApp(app.id);
      } catch (e) {
        setOpening(null);
        toast({ title: `Couldn’t open ${app.name} on ${machineName()}`, body: errMsg(e), kind: 'error' });
        return;
      }
      await refresh();
    },
    [refresh],
  );

  return { windows, loaded, opening: opening?.app ?? null, closing, focus, close, launch };
}

/** The window's own title without its app's name on the end ("Inbox - Google Chrome" → "Inbox"). */
export function shortTitle(w: Pick<DesktopWindow, 'title' | 'app'>): string {
  let t = w.title.trim();
  for (const sep of [' - ', ' — ', ' – ']) if (t.endsWith(sep + w.app)) t = t.slice(0, -(sep.length + w.app.length));
  return t || w.app;
}

/** The app's icon, or its initial on a tile when it has none (or it doesn't load). */
function AppIcon({ name, icon, size }: { name: string; icon?: string; size: number }) {
  const [broken, setBroken] = useState(false);
  const style = { '--icon': `${size}px` } as CSSProperties;
  if (icon && !broken) return <img className="dock-icon" src={icon} alt="" draggable={false} style={style} onError={() => setBroken(true)} />;
  return (
    <span className="dock-icon dock-mono" style={style} aria-hidden="true">
      {(name.trim()[0] ?? '?').toUpperCase()}
    </span>
  );
}


type Item = { id: string; win?: DesktopWindow; opening?: DesktopApp };

/**
 * Items that went keep their place for a moment, marked `leaving`, so they can animate out.
 * A gone item goes back in after the item it followed.
 */
function useLeaving(items: Item[]): Array<Item & { leaving?: boolean }> {
  const [gone, setGone] = useState<Array<{ item: Item; after: string | null }>>([]);
  const prev = useRef(items);
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  useEffect(() => {
    const now = new Set(items.map((i) => i.id));
    const was = prev.current;
    prev.current = items;
    const went = was.flatMap((item, k) => (now.has(item.id) ? [] : [{ item, after: k > 0 ? was[k - 1].id : null }]));
    // One that came back isn't leaving any more.
    setGone((g) => {
      const kept = g.filter((x) => !now.has(x.item.id));
      return went.length || kept.length !== g.length ? [...kept, ...went] : g;
    });
    if (!went.length) return;
    const ids = new Set(went.map((x) => x.item.id));
    const t = setTimeout(() => {
      timers.current.delete(t);
      setGone((g) => g.filter((x) => !ids.has(x.item.id)));
    }, LEAVE_MS);
    timers.current.add(t);
  }, [items]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  return useMemo(() => {
    if (!gone.length) return items;
    const out: Array<Item & { leaving?: boolean }> = [];
    const put = (after: string | null) => gone.filter((g) => g.after === after).forEach((g) => out.push({ ...g.item, leaving: true }));
    put(null);
    for (const it of items) {
      out.push(it);
      put(it.id);
    }
    const placed = new Set(out.map((i) => i.id));
    gone.filter((g) => !placed.has(g.item.id)).forEach((g) => out.push({ ...g.item, leaving: true }));
    return out;
  }, [items, gone]);
}

/**
 * The windows, in a row that scrolls sideways inside the bar (with faded edges where there's
 * more), and a highlight that slides to the window in front.
 */
function WindowList({
  windows,
  opening,
  closing,
  loaded,
  disabled,
  onFocus,
  onClose,
}: {
  windows: DesktopWindow[];
  opening: DesktopApp | null;
  closing: ReadonlySet<string>;
  loaded: boolean;
  disabled: boolean;
  onFocus(w: DesktopWindow): void;
  onClose(w: DesktopWindow): void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLSpanElement>(null);
  const items = useMemo<Item[]>(() => {
    const out: Item[] = windows.map((w) => ({ id: w.id, win: w }));
    if (opening) out.push({ id: `opening:${opening.id}`, opening });
    return out;
  }, [windows, opening]);
  const shown = useLeaving(items);
  const activeId = windows.find((w) => w.active)?.id;
  // Several windows of one app: each says its own title instead of the app's name.
  const perApp = useMemo(() => {
    const n = new Map<string, number>();
    windows.forEach((w) => n.set(w.app, (n.get(w.app) ?? 0) + 1));
    return n;
  }, [windows]);

  // Where the row can scroll to: its edges fade on the sides that have more.
  const edges = useCallback(() => {
    const s = scrollRef.current;
    if (!s) return;
    s.dataset.fadeStart = s.scrollLeft > 1 ? '1' : '0';
    s.dataset.fadeEnd = s.scrollLeft + s.clientWidth < s.scrollWidth - 1 ? '1' : '0';
  }, []);

  // The highlight sits under the window in front; it slides when that changes (and appears in
  // place, without sliding in from the start).
  const place = useCallback(() => {
    const g = glowRef.current;
    const el = activeId ? trackRef.current?.querySelector<HTMLElement>(`[data-window-id="${CSS.escape(activeId)}"]:not(.is-leaving)`) : null;
    if (!g) return;
    if (!el) {
      g.dataset.on = '0';
      return;
    }
    const jump = g.dataset.on !== '1';
    if (jump) g.style.transition = 'none';
    g.style.setProperty('--x', `${el.offsetLeft}px`);
    g.style.setProperty('--w', `${el.offsetWidth}px`);
    g.dataset.on = '1';
    if (jump) {
      void g.offsetWidth;
      g.style.transition = '';
    }
  }, [activeId]);

  useLayoutEffect(() => {
    place();
    edges();
  });

  useEffect(() => {
    const ro = new ResizeObserver(() => {
      place();
      edges();
    });
    if (scrollRef.current) ro.observe(scrollRef.current);
    if (trackRef.current) ro.observe(trackRef.current);
    return () => ro.disconnect();
  }, [place, edges]);

  // The window in front, and an app being opened, stay in view (a window opened past the
  // edge, a focus from elsewhere).
  const reveal = useCallback((selector: string) => {
    const s = scrollRef.current;
    const el = s?.querySelector<HTMLElement>(selector);
    if (!s || !el) return;
    const pad = 24;
    const left = el.offsetLeft - pad;
    const right = el.offsetLeft + el.offsetWidth + pad - s.clientWidth;
    const to = left < s.scrollLeft ? left : right > s.scrollLeft ? right : null;
    if (to !== null) s.scrollTo({ left: Math.max(0, to), behavior: reducedMotion() ? 'auto' : 'smooth' });
  }, []);
  useEffect(() => {
    if (activeId) reveal(`[data-window-id="${CSS.escape(activeId)}"]`);
  }, [activeId, windows.length, reveal]);
  useEffect(() => {
    if (opening) reveal('.dock-opening:not(.is-leaving)');
  }, [opening, reveal]);

  return (
    <div
      className="dock-scroll"
      ref={scrollRef}
      onScroll={edges}
      // A mouse wheel scrolls the row sideways.
      onWheel={(e) => {
        const s = scrollRef.current;
        if (s && Math.abs(e.deltaY) > Math.abs(e.deltaX) && s.scrollWidth > s.clientWidth) s.scrollLeft += e.deltaY;
      }}
      data-testid="desktop-windows"
    >
      <div className="dock-track" ref={trackRef}>
        <span className="dock-glow" ref={glowRef} aria-hidden="true" />
        {shown.map((it) => {
          if (it.win) {
            const w = it.win;
            const label = (perApp.get(w.app) ?? 0) > 1 ? shortTitle(w) : w.app;
            const off = disabled || !!it.leaving || closing.has(w.id);
            return (
              <div
                key={it.id}
                className={`dock-win dock-window ${w.active ? 'is-active' : ''} ${it.leaving ? 'is-leaving' : ''} ${closing.has(w.id) ? 'is-closing' : ''} ${disabled ? 'is-disabled' : ''}`}
                title={w.title || w.app}
                data-testid="desktop-window"
                data-window-id={it.id}
                data-active={w.active ? '1' : '0'}
              >
                <button
                  className="dock-win-main"
                  aria-current={w.active ? 'true' : undefined}
                  disabled={off}
                  onClick={() => !w.active && onFocus(w)}
                  // A middle click closes it, as in a browser's tabs.
                  onMouseDown={(e) => e.button === 1 && e.preventDefault()}
                  onAuxClick={(e) => e.button === 1 && !off && onClose(w)}
                >
                  <AppIcon name={w.app} icon={w.icon} size={16} />
                  <span className="dock-label">{label}</span>
                </button>
                <button className="dock-win-close" aria-label={`Close ${label}`} title="Close" disabled={off} onClick={() => onClose(w)} data-testid="desktop-window-close">
                  <Ic icon={IconX} size={12} />
                </button>
              </div>
            );
          }
          return (
            <span key={it.id} role="status" className={`dock-win dock-opening ${it.leaving ? 'is-leaving' : ''}`} aria-live="polite" data-testid="desktop-opening">
              <Mark tone="run" blink />
              <span className="dock-label">Opening {it.opening!.name}…</span>
            </span>
          );
        })}
        {loaded && !shown.length && <span className="dock-none">No windows open</span>}
      </div>
    </div>
  );
}

/** Closes on Escape, a press outside `ref`, or a press in the stream. Not when the stream just
 * takes the focus (taking control, a reconnect): that closed it as it opened. */
function useDismiss(ref: RefObject<HTMLElement | null>, onClose: () => void) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const down = (e: PointerEvent) => ref.current && !ref.current.contains(e.target as Node) && close.current();
    const key = (e: KeyboardEvent) => e.key === 'Escape' && close.current();
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('keydown', key);
    const offFrame = onFramePress(() => close.current());
    return () => {
      document.removeEventListener('pointerdown', down, true);
      document.removeEventListener('keydown', key);
      offFrame();
    };
  }, [ref]);
}

// The last list, so the launcher opens already filled (it looks again each time).
let appsCache: DesktopApp[] | null = null;

/** The machine's installed apps as a grid (and a search field when there are many). */
function AppsLauncher({
  sheet,
  pres,
  onLaunch,
  onClose,
  anchor,
}: {
  sheet: boolean;
  /** The popover's presence (a .ds-float): grows from the Apps button in, leaves quicker. */
  pres: { shown: boolean; leaving: boolean };
  onLaunch(app: DesktopApp): void;
  onClose(): void;
  anchor: RefObject<HTMLElement | null>;
}) {
  const [apps, setApps] = useState<DesktopApp[] | null>(appsCache);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const searchable = (apps?.length ?? 0) > SEARCH_MIN;
  // The Apps button toggles the popover itself: a press on it isn't "outside".
  const inside = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    inside.current = sheet ? panelRef.current : anchor.current;
  });
  useDismiss(inside, onClose);

  useEffect(() => {
    let live = true;
    desktopApps()
      .then((a) => {
        appsCache = a;
        if (live) setApps(a);
      })
      .catch((e) => live && setError(errMsg(e)));
    return () => {
      live = false;
    };
  }, []);

  // A keyboard goes to the search field; a touch screen keeps its keyboard down until asked.
  useEffect(() => {
    if (searchable && !matches(COARSE_MQ)) searchRef.current?.focus();
  }, [searchable]);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!apps || !s) return apps ?? [];
    return apps.filter((a) => a.name.toLowerCase().includes(s) || a.id.toLowerCase().includes(s));
  }, [apps, q]);

  let body: ReactNode;
  if (error && !apps) body = <div className="dock-apps-note">Couldn’t list {machineName()}’s apps: {error}</div>;
  else if (!apps) body = <div className="dock-apps-note loading-label">loading apps</div>;
  else if (!shown.length) body = <div className="dock-apps-note">{q ? `No app matches “${q.trim()}”` : 'No apps installed'}</div>;
  else
    body = (
      <div className="dock-apps-grid">
        {shown.map((a, i) => (
          <button
            key={a.id}
            className="dock-app"
            style={{ '--i': i } as CSSProperties}
            title={a.name}
            onClick={() => onLaunch(a)}
            data-testid="desktop-app"
            data-app-id={a.id}
          >
            <AppIcon name={a.name} icon={a.icon} size={sheet ? 44 : 36} />
            <span className="dock-app-name">{a.name}</span>
          </button>
        ))}
      </div>
    );

  const search = searchable && (
    <input
      ref={searchRef}
      className="dock-apps-search"
      type="search"
      placeholder="Search apps"
      value={q}
      onChange={(e) => setQ(e.target.value)}
      onKeyDown={(e) => e.key === 'Enter' && shown[0] && onLaunch(shown[0])}
      aria-label="Search apps"
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      data-testid="desktop-apps-search"
    />
  );

  if (sheet)
    return (
      <div className="palette-backdrop palette-sheet" onClick={onClose} data-testid="desktop-apps-sheet">
        <div className="palette dock-apps is-sheet" ref={panelRef} role="dialog" aria-modal="true" aria-label="Open an app" onClick={(e) => e.stopPropagation()} data-testid="desktop-apps">
          <div className="tsheet-head">
            <span className="tsheet-title">Open an app</span>
            <button className="palette-cancel" onClick={onClose} data-testid="desktop-apps-cancel">
              Cancel
            </button>
          </div>
          {search}
          {body}
        </div>
      </div>
    );
  return (
    <div className="dock-apps is-popover ds-float" ref={panelRef} role="dialog" aria-label="Open an app" {...presence(pres)} data-testid="desktop-apps">
      <div className="dock-apps-head">
        <span>Open an app</span>
        {apps && <span className="dock-apps-count">{apps.length}</span>}
      </div>
      {search}
      {body}
    </div>
  );
}

const IDLE_MS = 3000;

/** Over the stream the dock steps back once nothing has touched it for a while: the pointer, the keyboard, the
 *  apps menu or a window opening, closing or coming to the front brings it back. */
function useDockIdle(open: boolean, windows: string) {
  const [on, setOn] = useState(false);
  const inside = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const wake = useCallback(() => {
    setOn(false);
    clearTimeout(timer.current);
    if (!inside.current) timer.current = setTimeout(() => setOn(true), IDLE_MS);
  }, []);
  useEffect(() => {
    if (open) {
      inside.current = true;
      wake();
      return () => {
        inside.current = false;
        wake();
      };
    }
  }, [open, wake]);
  useEffect(wake, [windows, wake]);
  useEffect(() => () => clearTimeout(timer.current), []);
  return {
    on,
    hold: () => {
      inside.current = true;
      wake();
    },
    release: () => {
      inside.current = open;
      wake();
    },
  };
}

/**
 * The dock over the desktop stream (a floating bar; on phones, under it). `live`: the desktop runs and the connection is up (it
 * looks at the windows only then). `viewOnly`: another tab or device drives the desktop, so
 * the windows show but switching and opening wait for Take control.
 */
export function DesktopDock({ live, viewOnly, sheet }: { live: boolean; viewOnly: boolean; sheet: boolean }) {
  const d = useDesktopWindows(live);
  const [apps, setApps] = useState(false);
  const launcher = usePresence(apps, 120); // --dur-exit-sm
  const appsBtn = useRef<HTMLDivElement>(null);
  const hint = viewOnly ? ' (take control first)' : '';
  const idle = useDockIdle(apps, d.windows.map((w) => `${w.id}${w.active ? '*' : ''}`).join() + (d.opening ? '+' : ''));
  return (
    <nav className="dock" aria-label="Desktop windows" data-testid="desktop-dock" data-idle={idle.on ? '' : undefined} onPointerEnter={idle.hold} onPointerLeave={idle.release} onFocus={idle.hold} onBlur={idle.release}>
      <div className="dock-lead" ref={appsBtn}>
        <button
          className={`dock-apps-btn ${apps ? 'is-open' : ''}`}
          onClick={() => setApps((o) => !o)}
          disabled={viewOnly}
          aria-haspopup="dialog"
          aria-expanded={apps}
          title={`Open an app${hint}`}
          data-testid="desktop-apps-open"
        >
          <Ic icon={IconLayoutGrid} size={16} />
          <span>Apps</span>
        </button>
        {(sheet ? apps : launcher.mounted) && (
          <AppsLauncher
            sheet={sheet}
            pres={launcher}
            anchor={appsBtn}
            onClose={() => setApps(false)}
            onLaunch={(a) => {
              setApps(false);
              void d.launch(a);
            }}
          />
        )}
      </div>
      <span className="dock-sep" aria-hidden="true" />
      <WindowList
        windows={d.windows}
        opening={d.opening}
        closing={d.closing}
        loaded={d.loaded}
        disabled={viewOnly}
        onFocus={(w) => void d.focus(w)}
        onClose={(w) => void d.close(w)}
      />
    </nav>
  );
}
